import { useState, useRef, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import Fuse from 'fuse.js';
import {
  useAdaptiveDropdownPosition,
  DropdownPosition,
} from '@taleemabad/shared';
import { List, SearchField, Typography } from '@taleemabad/dsm/components';

export interface SuggestionItem {
  id: string;
  label: string;
  name?: string;
}

// Re-export for backward compatibility
export { DropdownPosition as SuggestionPosition } from '@taleemabad/shared';

interface SearchWithSuggestionsProps {
  value?: string;
  suggestions: SuggestionItem[];
  onSearchChange?: (query: string) => void;
  onSuggestionClick?: (item: SuggestionItem) => void;
  onChange?: (item: SuggestionItem | null) => void;
  placeholder?: string;
  disabled?: boolean;
  showDropdownOnFocus?: boolean;
  dropdownFix?: boolean;
  suggestionsPosition?: DropdownPosition;
  // When true, if component is merely displaying a prefilled value (no user typing yet),
  // keep showing the full suggestions list instead of filtering down to the single matched item.
  preserveFullListOnPrefill?: boolean;
  'data-cy'?: string;
}

export const SearchWithSuggestions = ({
  value,
  suggestions,
  onSearchChange,
  onSuggestionClick,
  onChange,
  placeholder = 'Search',
  disabled = false,
  dropdownFix = true,
  showDropdownOnFocus = false,
  suggestionsPosition = DropdownPosition.Bottom,
  preserveFullListOnPrefill = false,
  'data-cy': dataCy,
}: SearchWithSuggestionsProps) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userHasTyped, setUserHasTyped] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentPosition = useAdaptiveDropdownPosition(
    containerRef,
    showSuggestions,
    suggestionsPosition,
  );

  const fuse = useMemo(
    () =>
      new Fuse(suggestions, {
        keys: ['label'],
        threshold: 0.4,
      }),
    [suggestions],
  );

  const filteredSuggestions = useMemo(() => {
    // If preserving full list on prefill and user hasn't typed yet, show all
    if (preserveFullListOnPrefill && !userHasTyped) return suggestions;
    if (query.trim() === '') return suggestions;
    return fuse.search(query).map((result) => result.item);
  }, [fuse, query, suggestions, preserveFullListOnPrefill, userHasTyped]);

  const handleSearchChange = (val: string) => {
    setUserHasTyped(true);
    setQuery(val);
    onSearchChange?.(val);
    if (val.trim() === '') {
      onChange?.(null);
    }
    // Simple logic: show if we have suggestions and (there's input OR showDropdownOnFocus is enabled)
    setShowSuggestions(
      suggestions.length > 0 && (val.trim() !== '' || showDropdownOnFocus),
    );
  };

  const handleClearClick = () => {
    setQuery('');
    setShowSuggestions(false);
    onChange?.(null);
  };

  const handleSuggestionClick = (item: SuggestionItem) => {
    onSuggestionClick?.(item);
    onChange?.(item);
    setQuery(item.label);
    setShowSuggestions(false);
  };

  const handleFocus = () => {
    // Always show suggestions on focus if we have suggestions, regardless of showDropdownOnFocus prop
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Clear dropdown position changes automatically handled by hook; no local effect needed.

  useEffect(() => {
    if (value !== undefined) {
      setQuery(value);
      // Reset typed state when external value changes so we treat it as a fresh prefill
      setUserHasTyped(false);
    }
  }, [value]);

  return (
    <StyledWrapper ref={containerRef} data-cy={dataCy}>
      {showSuggestions && currentPosition === DropdownPosition.Top && (
        <DropdownContent
          dropdownFix={dropdownFix}
          suggestionsPosition={currentPosition}
          filteredSuggestions={filteredSuggestions}
          handleSuggestionClick={handleSuggestionClick}
        />
      )}
      <SearchField
        value={query}
        label={placeholder}
        onChange={handleSearchChange}
        onClearClick={handleClearClick}
        disabled={disabled}
        onFocus={() => {
          handleFocus();
        }}
        data-cy={dataCy ? `${dataCy}-input` : undefined}
      />
      {showSuggestions && currentPosition === DropdownPosition.Bottom && (
        <DropdownContent
          dropdownFix={dropdownFix}
          suggestionsPosition={currentPosition}
          filteredSuggestions={filteredSuggestions}
          handleSuggestionClick={handleSuggestionClick}
        />
      )}
    </StyledWrapper>
  );
};

interface DropdownContentProps {
  dropdownFix: boolean;
  suggestionsPosition: DropdownPosition;
  filteredSuggestions: SuggestionItem[];
  handleSuggestionClick: (item: SuggestionItem) => void;
}

const DropdownContent = ({
  dropdownFix,
  suggestionsPosition,
  filteredSuggestions,
  handleSuggestionClick,
}: DropdownContentProps) => (
  <StyledDropdown
    isFixed={dropdownFix}
    data-position={suggestionsPosition}
    role="listbox"
  >
    {filteredSuggestions.length > 0 ? (
      filteredSuggestions.map((item) => (
        <List
          key={`${item.id}-${item.label}`}
          heading={item.label}
          onClick={() => handleSuggestionClick(item)}
          data-cy={`dropdown-option-${item.id}`}
        />
      ))
    ) : (
      <Typography variant="textBodySmallSchemesError">
        No results found.
      </Typography>
    )}
  </StyledDropdown>
);

const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
`;
interface StyledDropdownProps {
  isFixed: boolean;
}

const StyledDropdown = styled.div<StyledDropdownProps>`
  position: ${(props) => (props.isFixed ? 'absolute' : '')};
  ${(props) =>
    props.isFixed
      ? `
    left: 0;
    width: 100%;
  `
      : ''}
  z-index: 1000;
  box-shadow: var(--md-sys-color-shadow);
  border-radius: var(--rk-border-radius-lg);
  max-height: 10rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background-color: var(--md-sys-color-on-primary);
  box-sizing: border-box;
  padding: var(--rk-spacing-md);
  /* Position variants */
  &[data-position='bottom'] {
    top: 100%;
    padding-top: 0.5rem;
  }
  &[data-position='top'] {
    bottom: 100%;
    margin-bottom: 0.5rem;
  }
`;
